/**
 * 图片加载逻辑
 */
import { MAX_CONCURRENT_LOAD_TASKS, LOAD_TIMEOUT_MS, RELOAD_POLL_INTERVAL_MS, RELOAD_MAX_POLL_ATTEMPTS } from '../utils/constants';
import ImageReloader from '@/components/st_comps/st_comp/utils/ImageReloader';

/**
 * 判断是否为审核目标图片
 * @param {*} image - 图片对象
 * @returns {boolean}
 */
function isAuditTarget(image) {
  return Boolean(image && typeof image === 'object' && image.im);
}

export function useImageLoader() {
  const activeLoadTimeouts = new Set();

  return {
    /**
     * 开始加载图片队列
     * @param {Array} images - 图片数组
     * @param {object} options - 选项
     * @param {Function} options.onUpdate - 单张图片加载完成回调 (image, imageObject, isError) => void
     * @param {Function} options.onProgress - 进度更新回调 (finishedCount, totalCount) => void
     * @param {Function} options.onStatusChange - 状态文本变化回调 (text) => void
     * @param {Function} options.onEnd - 全部加载完成回调 () => void
     * @param {Function} options.onReloadPolling - 存在 errorImages 时回调 (errorImages) => void
     * @param {Function} options.onAbnormal - 加载异常回调 (err, image) => void
     * @param {Function} options.onTimeout - 单张图片加载超时回调 (image, url) => void
     * @param {object} tokenRef - 用于取消加载的 token 引用 { current: number }
     * @param {Function} isTokenValid - 判断 token 是否有效的函数 (currentToken) => boolean
     * @returns {object} { loadToken }
     */
    startLoadQueue(
      images,
      { onUpdate, onProgress, onStatusChange, onEnd, onReloadPolling, onAbnormal, onTimeout },
      tokenRef,
      isTokenValid
    ) {
      const loadQueue = Array.isArray(images) ? images.map((image, index) => ({ image, index })) : [];
      const loadToken = Date.now();
      tokenRef.current = loadToken;

      if (!loadQueue.length) {
        if (typeof onEnd === 'function') {
          onEnd();
        }
        return { loadToken };
      }

      let runningCount = 0;
      let nextTaskIndex = 0;
      let loadFinishedCount = 0;
      const loadTotalCount = loadQueue.length;
      const errorImages = [];

      const canContinue = () => isTokenValid(loadToken);

      const finalizeTask = () => {
        if (!canContinue()) {
          return;
        }

        runningCount = runningCount > 0 ? runningCount - 1 : 0;
        loadFinishedCount++;

        if (typeof onProgress === 'function') {
          onProgress(loadFinishedCount, loadTotalCount);
        }

        if (loadFinishedCount === loadTotalCount && runningCount === 0 && nextTaskIndex >= loadQueue.length) {
          if (errorImages.length > 0) {
            if (typeof onReloadPolling === 'function') {
              onReloadPolling(errorImages);
            }
            return;
          }
          if (typeof onEnd === 'function') {
            onEnd();
          }
          return;
        }

        pumpQueue();
      };

      const handleLoadComplete = (image, imageObject, isError, index, reason = 'onerror') => {
        if (!canContinue()) {
          return;
        }

        if (isError) {
          // 阶段1：加载失败，收集 errorImages，不设置 orisize
          errorImages.push({
            image: image,
            imageObject: imageObject,
            url: image.im || image.src || image,
            index: index,
            reason: reason
          });
          finalizeTask();
          return;
        }

        if (isAuditTarget(image)) {
          image.orisize = [imageObject.width, imageObject.height];
        }

        if (typeof onUpdate === 'function') {
          onUpdate(image, imageObject, false);
        }
        finalizeTask();
      };

      const runLoadTask = (task) => {
        let im = null;
        let timeoutId = null;
        let completed = false;
        const image = task.image;

        const finish = (imageObject, isError, reason = 'onerror') => {
          if (completed) {
            return;
          }
          completed = true;
          if (timeoutId) {
            clearTimeout(timeoutId);
            activeLoadTimeouts.delete(timeoutId);
            timeoutId = null;
          }
          // 清理 Image 对象，避免超时后继续下载和内存占用
          if (im) {
            im.onload = null;
            im.onerror = null;
            im.src = '';
          }
          handleLoadComplete(image, imageObject, isError, task.index, reason);
        };

        try {
          if (typeof onStatusChange === 'function') {
            onStatusChange('正在加载图片');
          }

          const imageUrl = isAuditTarget(image) ? image.im : image;
          im = new Image();

          im.onload = function () {
            finish(this, false);
          };

          im.onerror = function () {
            finish(this, true, 'onerror');
          };

          timeoutId = setTimeout(() => {
            if (typeof onTimeout === 'function') {
              onTimeout(image, imageUrl);
            }
            finish(im || {}, true, 'timeout');
          }, LOAD_TIMEOUT_MS);
          activeLoadTimeouts.add(timeoutId);

          im.src = imageUrl;
        } catch (err) {
          if (typeof onAbnormal === 'function') {
            onAbnormal(err, image);
          }
          // 异常也视为加载失败，避免卡死
          finish(im || {}, true, 'onerror');
        }
      };

      const pumpQueue = () => {
        if (!canContinue()) {
          return;
        }

        while (runningCount < MAX_CONCURRENT_LOAD_TASKS && nextTaskIndex < loadQueue.length) {
          const task = loadQueue[nextTaskIndex];
          nextTaskIndex++;
          runningCount++;
          runLoadTask(task);
        }
      };

      pumpQueue();

      return { loadToken };
    },

    /**
     * 阶段2：轮询重载
     * @param {Array} imagesToReload - errorImages
     * @param {Object} options - { onUpdate, onProgress, onEnd, onAttempt, onAbnormal }
     * @returns {ImageReloader} - 返回实例，便于外部 destroy
     */
    reloadImagesWithPolling(imagesToReload, { onUpdate, onEnd, onAttempt, onAbnormal }) {
      const reloader = new ImageReloader({
        pollInterval: RELOAD_POLL_INTERVAL_MS,
        maxPollAttempts: RELOAD_MAX_POLL_ATTEMPTS
      });

      reloader.reloadBatch(imagesToReload, {
        onItemSuccess: (item, imageObject) => {
          if (typeof onUpdate === 'function') {
            onUpdate(item.image, imageObject, false);
          }
        },
        onItemError: (item) => {
          if (typeof onUpdate === 'function') {
            // 超时已设置 orisize=[300,300]，imageObject 为阶段1 的实例
            onUpdate(item.image, item.imageObject, true);
          }
        },
        onAttempt: (item, attempts, isSuccess, timedOut) => {
          if (typeof onAttempt === 'function') {
            onAttempt(item, attempts, isSuccess, timedOut);
          }
        }
      }).then((preheatResults) => {
        reloader.destroy();
        if (typeof onEnd === 'function') {
          onEnd(preheatResults);
        }
      }).catch((err) => {
        reloader.destroy();
        if (typeof onAbnormal === 'function') {
          onAbnormal(err);
        }
        if (typeof onEnd === 'function') {
          onEnd([]);
        }
      });

      return reloader;
    },

    /**
     * 取消所有活跃的 stage 1 加载超时定时器
     */
    cancelAllLoadTimeouts() {
      activeLoadTimeouts.forEach((id) => clearTimeout(id));
      activeLoadTimeouts.clear();
    },

    /**
     * 阶段2前置：批量请求归档图片解冻
     * @param {Array} imagesToReload - errorImages，每项包含 { url }
     * @param {Function} requestFn - 请求函数，签名 (url, params) => Promise
     * @returns {Promise<Array>} - 解冻结果数组
     */
    async restoreArchivedImages(imagesToReload, requestFn) {
      if (!Array.isArray(imagesToReload) || imagesToReload.length === 0) {
        return [];
      }

      const urls = imagesToReload.map(item => item.url).filter(Boolean);
      if (urls.length === 0) {
        return [];
      }

      try {
        if (typeof requestFn !== 'function') {
          return [];
        }

        const response = await requestFn('/imgcenter/restore_images', {
          urls: JSON.stringify(urls)
        });

        if (!response || response.errcode !== 0 || !Array.isArray(response.data)) {
          return [];
        }

        return response.data.map(item => ({
          url: item.url,
          from: item.from,
          provider: item.provider,
          bucket: item.bucket,
          path: item.path,
          status: item.status,
          msg: item.msg
        }));
      } catch (err) {
        return [];
      }
    }
  };
}
